import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import PlayPauseIcon from '../shared/PlayPauseIcon';
import { getQuizOptionsData } from '../../constants';

import stepSisSvg from '../../assets/StepSis.svg';
import stepMomSvg from '../../assets/StepMom.svg';
import girlfriendSvg from '../../assets/Girlfriend.svg';

const RELATIONSHIP_ICONS = {
  stepSis: stepSisSvg,
  stepMom: stepMomSvg,
  girlfriend: girlfriendSvg,
};

const Step5 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const { t } = useTranslation();
  const audioRefs = useRef({});
  const playingVoiceRef = useRef(null);
  const sliderRef = useRef();

  const voices = getQuizOptionsData('voice');
  const relationships = getQuizOptionsData('relationship');

  const [selectedVoice, setSelectedVoice] = useState(
    selections?.step5?.voice || voices[0]?.value || '',
  );
  const [selectedRelationship, setSelectedRelationship] = useState(
    selections?.step5?.relationship || relationships[0]?.value || '',
  );
  const [selectedAge, setSelectedAge] = useState(
    selections?.step5?.age || 22,
  );
  const [playingVoice, setPlayingVoice] = useState(null);

  const updateSliderBackground = (target, value) => {
    const min = Number(target.min);
    const max = Number(target.max);
    const percent = ((value - min) / (max - min)) * 100;
    target.style.backgroundRepeat = 'no-repeat, no-repeat';
    target.style.backgroundImage = `repeating-linear-gradient(-42deg, #b39ddb 0px, #b39ddb 8px, #8b5cf6 12px, #8b5cf6 18px, #b39ddb 22px), linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.05) 100%)`;
    target.style.backgroundSize = `${percent}% 100%, 100% 100%`;
  };

  useEffect(() => {
    if (!sliderRef.current) return;
    updateSliderBackground(sliderRef.current, selectedAge);
  }, [selectedAge]);

  const stopAllAudio = useCallback(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    playingVoiceRef.current = null;
    setPlayingVoice(null);
  }, []);

  const handleVoiceClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      stopAllAudio();
      setSelectedVoice(value);
    },
    [stopAllAudio, isTransitioning],
  );

  const handleRelationshipClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      setSelectedRelationship(value);
    },
    [isTransitioning],
  );

  const handlePlayVoice = useCallback(
    async (voice, event) => {
      event?.stopPropagation();
      if (isTransitioning) return;

      setSelectedVoice(voice.value);

      const audio = audioRefs.current[voice.value];
      if (!audio) return;

      if (playingVoiceRef.current === voice.value) {
        stopAllAudio();
        return;
      }

      stopAllAudio();
      await new Promise((resolve) => setTimeout(resolve, 50));

      try {
        playingVoiceRef.current = voice.value;
        setPlayingVoice(voice.value);
        await audio.play();

        audio.onended = () => {
          playingVoiceRef.current = null;
          setPlayingVoice(null);
        };
      } catch (err) {
        console.log('Play interrupted:', err);
        playingVoiceRef.current = null;
        setPlayingVoice(null);
      }
    },
    [stopAllAudio, isTransitioning],
  );

  const handleAgeChange = useCallback(
    (event) => {
      if (isTransitioning) return;
      const target = event.target;
      const value = Number(target.value);
      setSelectedAge(value);
      updateSliderBackground(target, value);
    },
    [isTransitioning],
  );

  const canContinue = selectedVoice && selectedRelationship && selectedAge;

  const handleContinue = useCallback(() => {
    if (isTransitioning || !canContinue) return;

    const voiceOption = voices.find((v) => v.value === selectedVoice);

    onAnswer('step5', {
      voice: selectedVoice,
      voiceAudio: voiceOption?.audio || null,
      relationship: selectedRelationship,
      age: selectedAge,
    });
  }, [
    canContinue,
    selectedVoice,
    selectedRelationship,
    selectedAge,
    voices,
    onAnswer,
    isTransitioning,
  ]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    onPrevious();
  }, [onPrevious, isTransitioning]);

  const renderRelationshipIcon = (value) => {
    const src = RELATIONSHIP_ICONS[value];
    if (!src) return null;
    return (
      <div className="icon-badge">
        <img src={src} alt={value} />
      </div>
    );
  };

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <QuizContent className="step4-design">
        <div className="step4-block">
          <QuizTitle>
            {t('choose')} <span>{t('voice_text')}</span>
          </QuizTitle>
          <QuizOptions className="voice-options">
            {voices.map((voice) => (
              <QuizOption
                key={voice.value}
                value={voice.value}
                name="voice"
                onClick={() => handleVoiceClick(voice.value)}
                className={`voice-option ${
                  selectedVoice === voice.value
                    ? 'selected'
                    : selectedVoice
                    ? 'not-selected'
                    : ''
                }`}
              >
                <div className="voice-card">
                  <div className="voice-controls">
                    <button
                      className="play-button"
                      onClick={(e) => handlePlayVoice(voice, e)}
                      type="button"
                    >
                      <PlayPauseIcon isPlaying={playingVoice === voice.value} />
                    </button>
                  </div>
                  <div className="voice-text">
                    <div className="voice-title">{t(voice.text)}</div>
                  </div>
                </div>
                <audio
                  ref={(el) => (audioRefs.current[voice.value] = el)}
                  src={voice.audio}
                  preload="metadata"
                />
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        <div className="step4-block">
          <QuizTitle>
            {t('choose')} <span>{t('relationships_text')}</span>
          </QuizTitle>
          <QuizOptions className="voice-options">
            {relationships.map((relationship) => (
              <QuizOption
                key={relationship.value}
                value={relationship.value}
                name="relationship"
                onClick={() => handleRelationshipClick(relationship.value)}
                className={`personality-option ${
                  selectedRelationship === relationship.value
                    ? 'selected'
                    : selectedRelationship
                    ? 'not-selected'
                    : ''
                }`}
              >
                <div className="personality-item">
                  <div className="personality-icon">
                    {renderRelationshipIcon(relationship.value)}
                  </div>
                  <div className="personality-info">
                    <div className="personality-title">
                      {t(relationship.text)}
                    </div>
                  </div>
                </div>
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        <div className="step1-block">
          <QuizTitle>{t('choose_age')}</QuizTitle>
          <div className="age-selector">
            <div className="age-selector-block">
              <div className="age-slider-container">
                <div className="age-slider-num">18+</div>
                <div className="age-slider-wrapper">
                  <input
                    ref={sliderRef}
                    type="range"
                    min="18"
                    max="55"
                    value={selectedAge}
                    onChange={handleAgeChange}
                    className="age-slider"
                    disabled={isTransitioning}
                  />
                  <div
                    className="age-thumb-label"
                    style={{
                      left: `calc(${
                        ((selectedAge - 18) / (55 - 18)) * 100
                      }% + ${
                        (0.5 - (selectedAge - 18) / (55 - 18)) * 48
                      }px)`,
                    }}
                  >
                    {selectedAge}
                  </div>
                </div>
                <div className="age-slider-num">55+</div>
              </div>
            </div>
          </div>
        </div>

        <div className="navigation-wrapper">
          <button
            className="previous-button"
            onClick={handlePrevious}
            disabled={isTransitioning}
          >
            {t('previous')}
          </button>
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!canContinue || isTransitioning}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step5;
